import axios, { AxiosInstance } from 'axios';

// En red: usa el mismo host que la página (ej. http://192.168.1.100:8004). En local: localhost:8004
export function getApiBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') return `${window.location.protocol}//${window.location.hostname}:8004`;
  return 'http://localhost:8004';
}
const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar respuestas y errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('name');
      localStorage.removeItem('role');
      
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/api/auth/login', { username, password });
    return response.data;
  },
};

// ===== ADMIN: permisos (tabla admin_users, lookup en Citisalud)
export interface UserLookupResponse {
  exists: boolean;
  cod_usuario?: string;
  nombre?: string;
}
export interface AdminItem {
  cod_usuario: string;
  nombre?: string;
}
export const adminAPI = {
  lookupUser: async (codigo: string): Promise<UserLookupResponse> => {
    const response = await api.get('/api/admin/users/lookup', {
      params: { codigo: codigo.trim().toUpperCase() },
    });
    return response.data;
  },
  getAdmins: async (): Promise<AdminItem[]> => {
    const response = await api.get('/api/admin/users');
    return response.data;
  },
  addAdmin: async (cod_usuario: string): Promise<AdminItem> => {
    const response = await api.post('/api/admin/users', {
      cod_usuario: cod_usuario.trim().toUpperCase(),
    });
    return response.data;
  },
  removeAdmin: async (cod_usuario: string): Promise<void> => {
    await api.delete(
      `/api/admin/users/${encodeURIComponent(cod_usuario.trim().toUpperCase())}`
    );
  },
};

// ===== TIPOS PARA PACIENTES =====

export interface PatientInfo {
  documento: string;
  nombre: string;
  sexo: string;
  fecha_nacimiento?: string;
  edad?: number;
  telefono: string;
}

export const patientsAPI = {
  getByDocument: async (documento: string): Promise<PatientInfo> => {
    const response = await api.get(`/api/patients/${documento}`);
    return response.data;
  },
};

// ===== TIPOS PARA PROFESIONAL =====

export interface ProfessionalInfo {
  nombre_profesional: string;
  rol_profesional: string;
  cargo_codigo?: string;
}

export const professionalAPI = {
  getCurrent: async (): Promise<ProfessionalInfo> => {
    const response = await api.get('/api/professional/current');
    return response.data;
  },
};

// ===== TIPOS PARA PLANTILLAS =====

export interface ConsentTemplate {
  id?: string;
  title: string;
  hospital_name: string;
  hospital_nit: string;
  document_type: string;
  subprocess: string;
  code: string;
  page_number: string;
  revision_date?: string;
  version: string;
  consent_text: string;
  // Sección de procedimientos
  procedure_title?: string;
  procedure_description?: string;
  benefits?: string;
  risks_complications?: string;
  alternatives?: string;
  implications?: string;
  recommendations?: string;
  // Sección de aceptación
  consent_acceptance_text?: string;
  digital_authorization_text?: string;
  // Sección de revocatoria
  revocation_text?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  is_active?: boolean;
}

export interface ConsentTemplateCreate {
  title: string;
  hospital_name?: string;
  hospital_nit?: string;
  document_type?: string;
  subprocess?: string;
  code?: string;
  page_number?: string;
  revision_date?: string;
  version?: string;
  consent_text: string;
  // Sección de procedimientos
  procedure_title?: string;
  procedure_description?: string;
  benefits?: string;
  risks_complications?: string;
  alternatives?: string;
  implications?: string;
  recommendations?: string;
  // Sección de aceptación
  consent_acceptance_text?: string;
  digital_authorization_text?: string;
  // Sección de revocatoria
  revocation_text?: string;
}

// ===== API PARA PLANTILLAS =====

export const templatesAPI = {
  getAll: async (activeOnly: boolean = true): Promise<ConsentTemplate[]> => {
    const response = await api.get('/api/templates', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  getById: async (id: string): Promise<ConsentTemplate> => {
    const response = await api.get(`/api/templates/${id}`);
    return response.data;
  },

  create: async (template: ConsentTemplateCreate): Promise<ConsentTemplate> => {
    const response = await api.post('/api/templates', template);
    return response.data;
  },

  update: async (id: string, template: ConsentTemplateCreate): Promise<ConsentTemplate> => {
    const response = await api.put(`/api/templates/${id}`, template);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/templates/${id}`);
  },

  generateContent: async (procedure_title: string): Promise<{
    procedure_title: string;
    procedure_description: string;
    benefits: string;
    risks_complications: string;
    alternatives: string;
    implications: string;
    recommendations: string;
  }> => {
    const response = await api.post('/api/templates/generate-content', { procedure_title });
    return response.data;
  },
};

// ===== TIPOS PARA CONSENTIMIENTOS COMPLETADOS =====

export interface ConsentForm {
  id?: string;
  template_id: string;
  template_title?: string;
  patient_data: {
    documento: string;
    nombre: string;
    sexo: string;
    fecha_nacimiento?: string;
    edad?: number;
    telefono: string;
    [key: string]: any;
  };
  patient_photo?: string | null;
  patient_fingerprint?: string | null;
  consent_responses: {
    accepts_consent: boolean | null;
    accepts_digital: boolean;
    [key: string]: any;
  };
  signatures: {
    patient_signature?: string | null;
    responsible_signature?: string | null;
    patient_name?: string;
    patient_document?: string;
    responsible_name?: string;
    responsible_document?: string;
    [key: string]: any;
  };
  filled_by?: string;
  filled_at?: string;
}

export interface ConsentFormCreate {
  template_id: string;
  patient_data: ConsentForm['patient_data'];
  patient_photo?: string | null;
  patient_fingerprint?: string | null;
  consent_responses: ConsentForm['consent_responses'];
  signatures: ConsentForm['signatures'];
}

export interface DataAuthorizationRequirement {
  required: boolean;
  reason?: string | null;
}

export interface DataAuthorizationRecord {
  id: string;
  patient_document: string;
  patient_data: Record<string, unknown>;
  responses: Record<string, unknown>;
  signatures: Record<string, unknown>;
  filled_by: string;
  filled_at: string;
}

export interface DataAuthorizationCreatePayload {
  patient_data: ConsentForm['patient_data'];
  responses: {
    accepts_treatment: boolean;
    accepts_sensitive_when_applicable: boolean;
    accepts_contact_channels: boolean;
  };
  signatures: ConsentForm['signatures'];
}

export const dataAuthorizationAPI = {
  getRequirement: async (
    documento: string,
    signal?: AbortSignal
  ): Promise<DataAuthorizationRequirement> => {
    const q = encodeURIComponent(documento.trim());
    const response = await api.get(`/api/data-authorization/requirement?documento=${q}`, {
      signal,
    });
    return response.data;
  },

  list: async (): Promise<DataAuthorizationRecord[]> => {
    const response = await api.get('/api/data-authorizations');
    return response.data;
  },

  create: async (body: DataAuthorizationCreatePayload): Promise<DataAuthorizationRecord> => {
    const response = await api.post('/api/data-authorization', body);
    return response.data;
  },

  downloadPDF: async (id: string): Promise<Blob> => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/api/data-authorization/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return response.data;
  },
};

export const consentFormsAPI = {
  create: async (form: ConsentFormCreate): Promise<ConsentForm> => {
    const response = await api.post('/api/consent-forms', form);
    return response.data;
  },

  /** Lista para auditoría (solo administradores) */
  getAuditList: async (): Promise<ConsentForm[]> => {
    const response = await api.get('/api/audit/consent-forms');
    return response.data;
  },

  getAll: async (documento?: string): Promise<ConsentForm[]> => {
    const response = await api.get('/api/consent-forms', {
      params: documento ? { documento: documento.trim() } : {},
    });
    return response.data;
  },

  downloadPDF: async (formId: string): Promise<Blob> => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/api/consent-forms/${formId}/pdf`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    });
    return response.data;
  },
};

// ===== ESTADÍSTICAS / DASHBOARD =====
export interface StatsPorMes {
  mes: string;
  anio: number;
  cantidad: number;
}
export interface StatsPorPlantilla {
  template_title: string;
  template_id: string;
  cantidad: number;
}
export interface StatsPorUsuario {
  filled_by: string;
  cantidad: number;
}
export interface DashboardStats {
  total: number;
  consentimientos: number;
  disentimientos: number;
  por_mes: StatsPorMes[];
  por_plantilla: StatsPorPlantilla[];
  por_usuario: StatsPorUsuario[];
}
export const statsAPI = {
  getDashboard: async (): Promise<DashboardStats> => {
    const response = await api.get('/api/stats/dashboard');
    return response.data;
  },
};

// ===== PLANTILLAS FAVORITAS =====
export const favoritesAPI = {
  getIds: async (): Promise<string[]> => {
    const response = await api.get('/api/templates/favorites');
    return response.data;
  },
  add: async (templateId: string): Promise<string[]> => {
    const response = await api.post('/api/templates/favorites', { template_id: templateId });
    return response.data;
  },
  remove: async (templateId: string): Promise<void> => {
    await api.delete(`/api/templates/favorites/${encodeURIComponent(templateId)}`);
  },
};

// ===== API PARA DIGITALPERSONA =====

export interface DigitalPersonaStatus {
  status: string;
  info: {
    initialized: boolean;
    win32_available: boolean;
    platform: string;
    python_version: string;
    engine_available?: boolean;
    initialization_attempt?: {
      success: boolean;
      message: string;
    };
    reader_status?: {
      connected: boolean;
      message: string;
    };
  };
  error?: string;
}

export interface DigitalPersonaCaptureResponse {
  status: string;
  message: string;
  template?: string;
  timestamp?: string;
}

export interface DigitalPersonaVerifyResponse {
  status: string;
  matches: boolean;
  message: string;
}

export const digitalPersonaAPI = {
  getStatus: async (): Promise<DigitalPersonaStatus> => {
    const response = await api.get('/api/digitalpersona/status');
    return response.data;
  },

  capture: async (timeout: number = 10000): Promise<DigitalPersonaCaptureResponse> => {
    const response = await api.post('/api/digitalpersona/capture', null, {
      params: { timeout }
    });
    return response.data;
  },

  verify: async (template: string, timeout: number = 10000): Promise<DigitalPersonaVerifyResponse> => {
    const response = await api.post('/api/digitalpersona/verify', {
      template,
      timeout
    });
    return response.data;
  },
};

// ===== API PARA DPFPApi (API NATIVA DE DIGITALPERSONA) =====

export interface DPFPApiStatus {
  status: string;
  info: {
    initialized: boolean;
    message: string;
    dll_path: string | null;
    devices: {
      count: number;
      list: string[];
      message: string;
      enum_success: boolean;
    };
  };
  error?: string;
}

export interface DPFPApiCaptureResponse {
  status: string;
  message: string;
  image: string;
  timestamp: string;
  method: string;
}

export interface DPFPApiCaptureImageResponse {
  success: boolean;
  image: string | null;
  message: string;
  timestamp: string;
}

export const dpfpapiAPI = {
  getStatus: async (): Promise<DPFPApiStatus> => {
    const response = await api.get('/api/dpfpapi/status');
    return response.data;
  },
  capture: async (timeout: number = 15000): Promise<DPFPApiCaptureResponse> => {
    const response = await api.post('/api/dpfpapi/capture', null, {
      params: { timeout }
    });
    return response.data;
  },
  captureImage: async (timeout: number = 15000): Promise<DPFPApiCaptureImageResponse> => {
    const response = await api.post('/api/dpfpapi/capture-image', null, {
      params: { timeout }
    });
    return response.data;
  }
};

// ===== API PARA FUTRONIC FS88 (ftrScanAPI) =====

export interface FutronicStatus {
  status: string;
  info?: {
    initialized: boolean;
    message?: string;
    sdk_path?: string;
    device?: string;
    device_open_ok?: boolean;
    device_message?: string;
    python_bits?: number;
  };
  error?: string;
}

export interface FutronicCaptureImageResponse {
  success: boolean;
  image: string | null;
  message: string;
  timestamp: string;
}

/** URL del puente local Windows (mismo esquema que /api en el backend), ej. http://127.0.0.1:14780 */
function getFutronicLocalBridgeBase(): string | null {
  const raw = process.env.REACT_APP_FUTRONIC_LOCAL_BRIDGE;
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  return String(raw).replace(/\/$/, '');
}

function getFutronicBridgeHeaders(): Record<string, string> {
  const t = process.env.REACT_APP_FUTRONIC_BRIDGE_TOKEN;
  if (t == null || String(t).trim() === '') {
    return {};
  }
  return { 'X-Futronic-Bridge-Token': String(t).trim() };
}

function fetchErrorToAxiosStyle(err: unknown, detail: string) {
  const e = new Error(detail) as Error & { response?: { data?: { detail?: string } } };
  e.response = { data: { detail } };
  e.message = err instanceof Error ? err.message : detail;
  return e;
}

export const futronicAPI = {
  getStatus: async (): Promise<FutronicStatus> => {
    const local = getFutronicLocalBridgeBase();
    if (local) {
      const r = await fetch(`${local}/api/futronic/status`, { headers: getFutronicBridgeHeaders() });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw fetchErrorToAxiosStyle(
          null,
          (j as { detail?: string }).detail || r.statusText || 'Error del puente Futronic local'
        );
      }
      return r.json() as Promise<FutronicStatus>;
    }
    const response = await api.get('/api/futronic/status');
    return response.data;
  },
  captureImage: async (): Promise<FutronicCaptureImageResponse> => {
    const local = getFutronicLocalBridgeBase();
    if (local) {
      const r = await fetch(`${local}/api/futronic/capture-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getFutronicBridgeHeaders() },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw fetchErrorToAxiosStyle(
          null,
          (j as { detail?: string }).detail || (j as { message?: string }).message || r.statusText
        );
      }
      return j as FutronicCaptureImageResponse;
    }
    const response = await api.post('/api/futronic/capture-image');
    return response.data;
  },
};

// ===== API PARA WINDOWS BIOMETRIC FRAMEWORK (WBF) =====

export interface WBFStatus {
  status: string;
  info: {
    sdk_type: string;
    api: string;
    available: boolean;
    initialized: boolean;
    session_active: boolean;
    initialization_attempt?: {
      success: boolean;
      message: string;
    };
    reader_status?: {
      connected: boolean;
      message: string;
    };
  };
  error?: string;
}

export interface WBFCaptureResponse {
  status: string;
  message: string;
  template?: string;
  timestamp?: string;
  method?: string;
}

export const wbfAPI = {
  getStatus: async (): Promise<WBFStatus> => {
    const response = await api.get('/api/wbf/status');
    return response.data;
  },

  capture: async (timeout: number = 10000): Promise<WBFCaptureResponse> => {
    const response = await api.post('/api/wbf/capture', null, {
      params: { timeout }
    });
    return response.data;
  },
};

export default api;
